import React, { useContext } from "react";
import { Box, Container, Grid, Typography, Card, IconButton, Avatar } from "@mui/material";
import Header from "components/Header/Header.js";
import HomeFooter from "components/Footer/HomeFooter.js";
import HeaderLinks from "components/Header/HeaderLinks.js";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram } from "lucide-react";
import { ThemeContext } from "components/Theme/ThemeContext";

export default function ContactUs(props) {
    const { t } = useTranslation();
    const { isDark, currentTheme } = useContext(ThemeContext);
    const { ...rest } = props;
    const settings = useSelector(state => state.settingsdata.settings);

    return (
        <Box sx={{ bgcolor: currentTheme.background, display: 'flex', flexDirection: 'column', transition: 'background-color 0.3s ease, color 0.3s ease' }}>
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Header
                    color="transparent"
                    routes={[]}
                    rightLinks={<HeaderLinks />}
                    fixed
                    changeColorOnScroll={{
                        height: 100,
                        color: isDark ? "dark" : "white"
                    }}
                    {...rest}
                />

                {/* Hero Section */}
                <Box sx={{
                    background: isDark ? 'linear-gradient(135deg, #0A0A0B 0%, #161619 100%)' : 'linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%)',
                    color: isDark ? 'white' : '#0F172A',
                    pt: 20,
                    pb: 12,
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: '20%',
                        right: '10%',
                        width: '300px',
                        height: '300px',
                        background: isDark ? 'radial-gradient(circle, rgba(233, 174, 49, 0.15) 0%, transparent 70%)' : 'radial-gradient(circle, rgba(233, 174, 49, 0.1) 0%, transparent 70%)',
                        borderRadius: '50%',
                        filter: 'blur(50px)',
                    }} />
                    <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
                        <Typography variant="h2" sx={{ fontWeight: 800, mb: 3, fontFamily: 'Outfit, sans-serif', color: isDark ? 'white' : '#0F172A' }}>
                            {t('contact_us')}
                        </Typography>
                        <Typography variant="h5" sx={{ opacity: 0.8, maxWidth: '800px', mx: 'auto', lineHeight: 1.6, color: isDark ? '#94a3b8' : '#475569' }}>
                            {t('get_in_touch_content') || "We'd love to hear from you."}
                        </Typography>
                    </Container>
                </Box>

                {/* Main Content */}
                <Container maxWidth="lg" sx={{ py: 8, flex: 1 }}>

                    {/* Company Info Header */}
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: isDark ? 'white' : '#1e293b', fontFamily: 'Outfit, sans-serif' }}>
                            {settings.CompanyName}
                        </Typography>
                        <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontSize: '1.1rem' }}>
                            {settings.CompanyAddress}
                        </Typography>
                    </Box>

                    <Grid container spacing={4} justifyContent="center">
                        {/* Email Card */}
                        {settings && settings.contact_email && (
                            <Grid item xs={12} md={4}>
                                <Card sx={{
                                    p: 4,
                                    height: '100%',
                                    background: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: '20px',
                                    border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.15)',
                                    boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                                    textAlign: 'center',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 10px 15px -3px rgba(0,0,0,0.1)' }
                                }}>
                                    <Avatar sx={{ bgcolor: isDark ? 'rgba(72, 127, 255, 0.1)' : '#eff6ff', color: '#487FFF', width: 64, height: 64, mx: 'auto', mb: 3 }}>
                                        <Mail size={32} />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: isDark ? 'white' : '#0F172A' }}>{t('email')}</Typography>
                                    <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b', wordBreak: 'break-all' }}>
                                        <a href={"mailto:" + settings.contact_email} style={{ color: 'inherit', textDecoration: 'none' }}>
                                            {settings.contact_email}
                                        </a>
                                    </Typography>
                                </Card>
                            </Grid>
                        )}

                        {/* Phone Card */}
                        {settings && settings.CompanyPhone && (
                            <Grid item xs={12} md={4}>
                                <Card sx={{
                                    p: 4,
                                    height: '100%',
                                    background: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff',
                                    backdropFilter: 'blur(12px)',
                                    borderRadius: '20px',
                                    border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.15)',
                                    boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                                    textAlign: 'center',
                                    transition: '0.3s',
                                    '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 10px 15px -3px rgba(0,0,0,0.1)' }
                                }}>
                                    <Avatar sx={{ bgcolor: isDark ? 'rgba(22, 163, 74, 0.1)' : '#f0fdf4', color: '#16a34a', width: 64, height: 64, mx: 'auto', mb: 3 }}>
                                        <Phone size={32} />
                                    </Avatar>
                                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: isDark ? 'white' : '#0F172A' }}>{t('phone')}</Typography>
                                    <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                        {settings.CompanyPhone}
                                    </Typography>
                                </Card>
                            </Grid>
                        )}

                        {/* Address Card */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{
                                p: 4,
                                height: '100%',
                                background: isDark ? 'rgba(30, 41, 59, 0.4)' : '#fff',
                                backdropFilter: 'blur(12px)',
                                borderRadius: '20px',
                                border: isDark ? '1px solid rgba(255, 255, 255, 0.05)' : '1px solid rgba(233, 174, 49, 0.15)',
                                boxShadow: isDark ? '0 8px 32px 0 rgba(0, 0, 0, 0.2)' : '0 8px 32px 0 rgba(233, 174, 49, 0.03)',
                                textAlign: 'center',
                                transition: '0.3s',
                                '&:hover': { transform: 'translateY(-5px)', boxShadow: isDark ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 10px 15px -3px rgba(0,0,0,0.1)' }
                            }}>
                                <Avatar sx={{ bgcolor: isDark ? 'rgba(234, 88, 12, 0.1)' : '#fff7ed', color: '#ea580c', width: 64, height: 64, mx: 'auto', mb: 3 }}>
                                    <MapPin size={32} />
                                </Avatar>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: isDark ? 'white' : '#0F172A' }}>{t('address')}</Typography>
                                <Typography variant="body1" sx={{ color: isDark ? '#94a3b8' : '#64748b' }}>
                                    {settings.CompanyAddress || t('address_placeholder')}
                                </Typography>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Social Media Section */}
                    <Box sx={{ textAlign: 'center', mt: 10 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, mb: 4, color: isDark ? 'white' : '#1e293b' }}>
                            {t('follow_us')}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3 }}>
                            {settings && settings.FacebookHandle && (
                                <IconButton
                                    href={settings.FacebookHandle}
                                    target="_blank"
                                    sx={{ bgcolor: '#3b5998', color: 'white', '&:hover': { bgcolor: '#2d4373', transform: 'translateY(-3px)' }, transition: 'all 0.2s', width: 56, height: 56 }}
                                >
                                    <Facebook size={24} />
                                </IconButton>
                            )}
                            {settings && settings.TwitterHandle && (
                                <IconButton
                                    href={settings.TwitterHandle}
                                    target="_blank"
                                    sx={{ bgcolor: '#00acee', color: 'white', '&:hover': { bgcolor: '#0084b4', transform: 'translateY(-3px)' }, transition: 'all 0.2s', width: 56, height: 56 }}
                                >
                                    <Twitter size={24} />
                                </IconButton>
                            )}
                            {settings && settings.InstagramHandle && (
                                <IconButton
                                    href={settings.InstagramHandle}
                                    target="_blank"
                                    sx={{ bgcolor: '#833AB4', color: 'white', '&:hover': { bgcolor: '#58277a', transform: 'translateY(-3px)' }, transition: 'all 0.2s', width: 56, height: 56 }}
                                >
                                    <Instagram size={24} />
                                </IconButton>
                            )}
                        </Box>
                    </Box>

                </Container>
            </Box>

            <HomeFooter />
        </Box>
    );
}